// @ts-check
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { slideOpen, slideClosed } from '@theme/custom.slide-reveal';

/** Two-level mobile menu overlay. Extending DialogComponent buys the native <dialog> a11y stack
 *  (focus trap, restore, Escape to close, scroll-lock with teardown) for free. */
class CustomMobileMenu extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.#prepare();
    // Track open/close through DialogComponent's events, not the open()/close() wrappers: Escape
    // and click-outside go straight to closeDialog and must still reset aria and the drill state.
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Mirror the connectedCallback binds so listeners don't stack if the
    // element reconnects; removing an unbound listener is a harmless no-op.
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  updatedCallback() {
    super.updatedCallback();
    // Theme-editor re-renders morph new Liquid into place; re-run the
    // (idempotent) preparation so fresh panels/tiles get hoisted and stamped.
    this.#prepare();
  }

  /** Opens the overlay. Bound via on:click="#custom-mobile-menu/open" on the hamburger. */
  open() {
    this.showDialog();
  }

  /** Closes the overlay. The native <dialog> restores focus to the hamburger. */
  close() {
    void this.closeDialog();
  }

  #handleDialogOpen = () => {
    for (const btn of document.querySelectorAll('.custom-mobile-menu__open-btn')) {
      btn.setAttribute('aria-expanded', 'true');
    }
  };

  /** Runs on dialog:close, after dialog.close(): the panel is already invisible, so resetting the
   *  drill for the next open does not flash L1 mid-close. */
  #handleDialogClose = () => {
    for (const btn of document.querySelectorAll('.custom-mobile-menu__open-btn')) {
      btn.setAttribute('aria-expanded', 'false');
    }
    this.#clearActivePanels();
    for (const submenu of this.querySelectorAll('.custom-mobile-menu__submenu[open]')) {
      submenu.removeAttribute('open');
    }
    delete this.dataset.level;
  };

  /**
   * Activates the L2 panel whose data-panel matches the clicked button.
   * Bound declaratively via on:click="/showPanel" on L1 nav row buttons.
   * @param {Event} event
   */
  showPanel(event) {
    const btn = event.target instanceof Element ? event.target.closest('[data-panel]') : null;
    if (!(btn instanceof HTMLElement) || !btn.dataset.panel) return;
    const panel = this.#activatePanelById(btn.dataset.panel);
    // The row that had focus is hidden with L1, so focus would otherwise drop to <body>.
    const backButton = panel?.querySelector('.custom-mobile-menu__back-btn');
    if (backButton instanceof HTMLElement) backButton.focus({ preventScroll: true });
  }

  /**
   * Accordion layout: clips a submenu open or closed with the reveal the PDP Load More controls
   * use. Bound via on:click="/toggleSubmenu" on the submenu's <summary>.
   * @param {Event} event
   */
  async toggleSubmenu(event) {
    event.preventDefault();
    const details = event.target instanceof Element ? event.target.closest('details') : null;
    const content = details?.querySelector(':scope > .custom-mobile-menu__l2-content');
    if (!(details instanceof HTMLDetailsElement) || !(content instanceof HTMLElement)) return;
    // A tap mid-slide would start a second clip whose cleanup races the first one's overflow reset.
    if (content.getAnimations().length > 0) return;

    if (!details.open) {
      details.open = true;
      slideOpen(content);
      return;
    }

    // The content has to stay rendered until it reaches zero, so [open] is dropped last.
    details.dataset.closing = '';
    await slideClosed(content);
    delete details.dataset.closing;
    details.open = false;
  }

  /** Returns from L2 to L1. Bound via on:click="/back". */
  back() {
    const hadFocus = this.contains(document.activeElement);
    const id = this.querySelector('.custom-mobile-menu__panel--active')?.getAttribute('data-panel');
    this.#clearActivePanels();
    delete this.dataset.level;
    if (!hadFocus || !id) return;
    const row = this.querySelector(`.custom-mobile-menu__row[data-panel="${CSS.escape(id)}"]`);
    if (row instanceof HTMLElement) row.focus({ preventScroll: true });
  }

  #clearActivePanels() {
    for (const panel of this.querySelectorAll('.custom-mobile-menu__panel--active')) {
      panel.classList.remove('custom-mobile-menu__panel--active');
    }
    for (const row of this.querySelectorAll('.custom-mobile-menu__row[aria-expanded="true"]')) {
      row.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Activates the L2 panel with the given data-panel id, if it exists.
   * @param {string} id
   * @returns {HTMLElement | undefined}
   */
  #activatePanelById(id) {
    const panel = this.querySelector(
      `.custom-mobile-menu__panel--l2[data-panel="${CSS.escape(id)}"]`
    );
    if (!(panel instanceof HTMLElement)) {
      console.warn(
        `[custom-mobile-menu] no L2 panel for data-panel="${id}"; drill-in row is a dead tap`
      );
      return;
    }
    this.#clearActivePanels();
    panel.classList.add('custom-mobile-menu__panel--active');
    this.dataset.level = '2';
    this.querySelector(`.custom-mobile-menu__row[data-panel="${CSS.escape(id)}"]`)?.setAttribute(
      'aria-expanded',
      'true'
    );
    return panel;
  }

  /**
   * Theme editor: opens the drawer and drills to the panel containing (or
   * belonging to) the selected block, so admins see what they're editing.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    clearTimeout(this.#deselectCloseTimeout);
    // At 1024px and up the wrapper is display:none, and showModal() on it would put an invisible
    // modal in the top layer and pin the body, which reads as a frozen preview.
    if (window.innerWidth > this.maxWidth) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    this.open();

    if (this.dataset.layout === 'accordion') {
      const submenu =
        target.closest('.custom-mobile-menu__submenu') ??
        target.querySelector('.custom-mobile-menu__submenu');
      submenu?.setAttribute('open', '');
      return;
    }

    // Group / link / tile / item-CTA blocks live inside a (hoisted) panel.
    const panel = target.closest('.custom-mobile-menu__panel--l2');
    if (panel instanceof HTMLElement && panel.dataset.panel) {
      this.#activatePanelById(panel.dataset.panel);
      return;
    }

    const item = target.closest('.custom-mobile-menu__list-item');
    const btn = item?.querySelector('[data-panel]');
    if (btn instanceof HTMLElement && btn.dataset.panel) {
      this.#activatePanelById(btn.dataset.panel);
    } else {
      this.back();
    }
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deselectCloseTimeout;

  #handleBlockDeselect = () => {
    // Switching blocks fires deselect then select, and an immediate close() leaves dialog.open
    // true through the exit animation, so the follow-up open() bails. Defer it; select cancels it.
    this.#deselectCloseTimeout = setTimeout(() => this.close());
  };

  /** Idempotent by design: it runs again after every theme-editor re-render. */
  #prepare() {
    const drawer = this.refs.dialog;
    const l1List = this.querySelector('.custom-mobile-menu__list');
    if (!l1List) {
      console.warn('[custom-mobile-menu] missing list container; drawer will be empty');
      return;
    }

    // 1. Hoist L2 panels out of the L1 list: an absolutely positioned panel would be clipped by
    // the list's own overflow-y: auto.
    for (const panel of l1List.querySelectorAll('.custom-mobile-menu__panel--l2')) {
      drawer.appendChild(panel);
    }

    // 2. content_for emits tiles in one stream ahead of the Liquid CTA, so grouping them into a
    // container appended last is what yields the design order: groups, CTA, tiles.
    for (const content of drawer.querySelectorAll('.custom-mobile-menu__l2-content')) {
      const loose = content.querySelectorAll(':scope > .custom-mobile-menu__tile');
      let container = content.querySelector(':scope > .custom-mobile-menu__tiles');
      if (loose.length === 0 && !container) continue;
      if (!container) {
        container = document.createElement('div');
        container.className = 'custom-mobile-menu__tiles';
        content.appendChild(container);
      }
      for (const tile of loose) container.appendChild(tile);
      container.classList.toggle(
        'custom-mobile-menu__tiles--grid',
        Boolean(container.querySelector('.custom-mobile-menu__tile--span-1'))
      );
    }

    // 3. Stagger indexes. L1: rows, then CTA, then account as trailing beats.
    let l1Index = 0;
    for (const li of l1List.querySelectorAll(':scope > .custom-mobile-menu__list-item')) {
      if (li instanceof HTMLElement) li.style.setProperty('--stagger-i', String(l1Index++));
    }
    const l1CTA = this.querySelector('.custom-mobile-menu__l1-cta');
    if (l1CTA instanceof HTMLElement) l1CTA.style.setProperty('--stagger-i', String(l1Index++));
    const account = this.querySelector('.custom-mobile-menu__account');
    if (account instanceof HTMLElement) account.style.setProperty('--stagger-i', String(l1Index));

    // L2 rows take their beat in visual order, mirroring L1. A group also takes its first row's
    // index, so its ::before divider animates on that same beat.
    for (const content of drawer.querySelectorAll('.custom-mobile-menu__l2-content')) {
      let l2Index = 0;
      for (const child of content.children) {
        if (!(child instanceof HTMLElement)) continue;
        if (child.classList.contains('custom-mobile-menu__group')) {
          child.style.setProperty('--stagger-i', String(l2Index));
          for (const row of child.querySelectorAll('.custom-mobile-menu__group-heading, li')) {
            if (row instanceof HTMLElement) row.style.setProperty('--stagger-i', String(l2Index++));
          }
        } else if (child.classList.contains('custom-mobile-menu__tiles')) {
          for (const tile of child.children) {
            if (tile instanceof HTMLElement)
              tile.style.setProperty('--stagger-i', String(l2Index++));
          }
        } else {
          child.style.setProperty('--stagger-i', String(l2Index++));
        }
      }
    }

    if (l1List.querySelector('.custom-mobile-menu__list-item') === null) {
      console.warn('[custom-mobile-menu] no menu items authored; drawer is empty');
    }
  }
}

if (!customElements.get('custom-mobile-menu')) {
  customElements.define('custom-mobile-menu', CustomMobileMenu);
}
